<template>
  <module-template
    id="scrolltainer"
    title
    :canEdit="$store.state.runRenamePermission"
    canList
    :canCopy="$store.state.runCreatePermission"
    :canDelete="$store.state.runDeletePermission && !run?.lockUser"
    :canDownload="canExportOlp"
    :isDownloading="exportingOlp"
    :downloadToolTip="$t('global.action.download-olp')"
    @copyItem="copyRun"
    @deleteItem="deleteRun"
    @editItem="editRun"
    @list="listRuns"
    @download="onExportOlp"
  >
    <template #title>
      <v-row dense class="px-1" align="center">
        {{ $t("global.run.run") }}
        <v-icon color="white" class="px-3" size="9">pic-arrow-right</v-icon>
        <div class="mr-2" v-if="run">
          {{ "#" + run?.id }}
        </div>
        <v-text-field
          dark
          v-model="editingName"
          type="text"
          v-if="showEdit"
          style="width: 400px"
          :rules="[noTagsRule]"
        >
          <template #append-outer>
            <v-btn
              @click="onEditConfirm"
              class="mr-1"
              dark
              fab
              outlined
              small
              :disabled="!canEditName"
            >
              <v-icon>pic-check</v-icon>
            </v-btn>
            <v-btn @click="showEdit = false" dark fab outlined small>
              <v-icon>pic-close</v-icon>
            </v-btn>
          </template>
        </v-text-field>
        <div v-else>{{ run?.title }}</div>
      </v-row>
    </template>

    <v-row dense>
      <v-col>
        <basic-card-horizontal
          :headers="infoCardHeaders"
          :item="infoCardItem"
          v-if="run"
          min-width="600"
        >
          <template #[`item.bpInstance`]="{ item }">
            <span v-if="item.bpInstance">
              <v-chip class="mb-1" color="orange" dark small>
                {{ item.bpInstance[0] }}
              </v-chip>
              <router-link :to="bpRoute"> {{ item.bpInstance[1] }}</router-link>
              <v-tooltip v-if="run?.lockUser" top>
                <template #activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" small class="ml-1">
                    pic-lock-fill
                  </v-icon>
                </template>
                <span>{{ lockMessage }}</span>
              </v-tooltip>
            </span>
          </template>

          <template #[`item.task`]="{ item }">
            <router-link :to="taskRoute">{{ item.task }}</router-link>
          </template>

          <template #[`item.env`]="{ item }">
            <router-link :to="envRoute"> {{ item.env }} </router-link>
          </template>

          <template #[`item.createdByName`]="{ item }">
            <span v-if="item.isCreatedByDeleted">
              {{ $t("global.concepts.deleted-user") }}
            </span>

            <router-link
              class="ooliba-font-color-blue"
              v-else
              :to="createdByRoute"
            >
              {{ item.createdByName }}
            </router-link>
          </template>
        </basic-card-horizontal>
      </v-col>

      <v-col>
        <basic-card-horizontal
          :headers="propsCardHeaders"
          :item="propsCardItem"
          class="mb-2"
          min-width="600"
          v-if="run"
        />

        <status-card :run="run"></status-card>
      </v-col>
    </v-row>

    <v-overlay :value="run?.status === 'INIT'">
      <v-chip color="blue" dark>
        {{ $t("global.run.updating-dependencies") }}
      </v-chip>
    </v-overlay>

    <!-- Action launch / cancel -->
    <v-row class="py-3" dense v-if="!run?.lockUser">
      <v-col cols="auto">
        <v-tooltip top :disabled="!run?.isCreatedByDeleted">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <v-btn
                v-if="canStart"
                :disabled="run?.isCreatedByDeleted"
                @click="onStartRun"
                color="primary"
              >
                <v-icon left>pic-play-fill</v-icon>
                {{ $t("global.run.start-run") }}
              </v-btn>
            </span>
          </template>
          <span>{{ $t("global.msg.retry-deleted-run") }}</span>
        </v-tooltip>
      </v-col>

      <v-col cols="auto">
        <v-btn v-if="canCancel" @click="onCancelRun" color="error">
          <v-icon left>pic-close</v-icon>
          {{ $t("global.action.cancel") }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Run details -->
    <v-row dense>
      <v-col cols="12">
        <run-details v-if="run" :run="run" />
      </v-col>
    </v-row>
  </module-template>
</template>

<script>
import { downloadFile, get, put, post, remove } from "@/model/api";
import BasicCardHorizontal from "@/components/BasicCardHorizontal";
import { noTags } from "@/model/rules";
import { formatDate } from "@/model/util";

import RunDetails from "@/components/run/RunDetails";
import ModuleTemplate from "@/components/layout/ModuleTemplate";
import StatusCard from "@/components/run/StatusCard";

export default {
  name: "SimulationRun",

  components: {
    BasicCardHorizontal,
    ModuleTemplate,
    RunDetails,
    StatusCard,
  },

  data() {
    return {
      infoCardHeaders: [
        { text: this.$t("global.modules.module"), value: "module" },
        { text: this.$t("global.run.simulation"), value: "simulation" },

        {
          text: this.$t("global.business-process.task"),
          value: "task",
          class: "ooliba-font-color-blue",
        },

        { text: this.$t("global.concepts.created-on"), value: "dateCreated" },

        {
          text: this.$t("global.environment.environment"),
          value: "env",
          class: "ooliba-font-color-blue",
        },

        {
          text: this.$t("global.administration.owner"),
          value: "createdByName",
        },

        {
          text: this.$t("global.business-process.business-process"),
          value: "bpInstance",
          class: "ooliba-font-color-blue",
        },
      ],

      run: {},

      timerId: undefined,

      showEdit: false,
      editingName: undefined,

      noTagsRule: noTags(this),

      deleting: false,

      exportingOlp: false,
    };
  },

  computed: {
    lockMessage() {
      const parsedDate = formatDate(this.run?.lockDate);
      let userName = this.bpInstance?.lockUser;
      if (this.bpInstance?.isLockUserDeleted) {
        userName = this.$t("global.concepts.deleted-user");
      }
      return this.$t("global.business-process.lock-message", [
        parsedDate,
        userName,
      ]);
    },

    canEditName() {
      return typeof this.noTagsRule(this.editingName) !== "string";
    },

    taskRoute() {
      if (!this.run?.taskId) {
        return {};
      }
      return {
        name: "Process task",
        params: { taskId: this.run.taskId },
      };
    },

    bpRoute() {
      if (!this.run?.bpId) {
        return {};
      }
      return {
        name: "BP instance",
        params: { bpId: this.run.bpId },
      };
    },

    envRoute() {
      if (!this.run?.parentFileEnvId && !this.run?.fileEnvId) {
        return {};
      }
      return {
        name: "File env",
        params: {
          fileEnvId: this.run.fileEnvIsLocal
            ? this.run.parentFileEnvId
            : this.run.fileEnvId,
        },
      };
    },

    createdByRoute() {
      if (!this.run?.createdById) {
        return {};
      }
      return {
        name: "User",
        params: { userId: this.run.createdById },
      };
    },

    canCancel() {
      const status = this.run?.status;
      const requestedStatus = this.run?.requestedStatus;

      return (
        this.$store.state.runCreatePermission &&
        requestedStatus !== "CANCEL_REQUESTED" &&
        (status == "WAITING_FOR_DATA" ||
          status == "WAITING_FOR_PROPS" ||
          status == "READY_TO_START" ||
          status == "WAITING_FOR_EVALUATION" ||
          status == "REJECTED_DATA" ||
          status == "INVALID_DATA" ||
          status == "COMPUTING" ||
          status == "INITIALIZING" ||
          status == "SCHEDULED" ||
          status == "RUNNING")
      );
    },

    canStart() {
      return (
        this.$store.state.runCreatePermission &&
        this.run?.status === "READY_TO_START" &&
        this.run?.requestedStatus !== "RUN_REQUESTED"
      );
    },

    canExportOlp() {
      return (
        this.$store.state.olpExportPermission && this.run?.status !== "INIT"
      );
    },

    infoCardItem() {
      if (!this.run) {
        return;
      }
      const envName = this.run.fileEnvIsLocal
        ? this.run.parentFileEnvName
        : this.run.fileEnvName;
      const envColor = this.run.fileEnvIsLocal
        ? this.run.parentFileEnvColor
        : this.run.fileEnvColor;

      return {
        module: this.run?.module + " (v." + this.run?.moduleVersion + ")",
        simulation: this.run?.simulation,
        createdByName: this.run.createdBy,
        dateCreated: formatDate(this.run.dateCreated),
        isCreatedByDeleted: this.run.isCreatedByDeleted,
        env: envName,
        envColor: envColor,
        task: this.run?.taskVersionName
          ? this.run?.taskVersionName +
            " (v." +
            (this.run?.taskVersionVersionNumber + 1) +
            ")"
          : "",
        bpInstance: this.run?.bpTitle
          ? ["#" + this.run?.bpId, this.run?.bpTitle]
          : null,
      };
    },

    propsCardHeaders() {
      if (this.run?.props) {
        return Object.keys(this.run.props).map((prop) => {
          return { text: prop, value: prop };
        });
      }
      return [];
    },

    propsCardItem() {
      if (!this.run) {
        return;
      }
      const item = {};
      this.propsCardHeaders.forEach((header) => {
        item[header.value] = this.run.props[header.value];
      });

      return item;
    },
  },

  async created() {
    const runId = this.$route.params.runId;
    if (!runId) return;

    this.run = await this.getRun(runId);
    this.editingName = this.run?.title;
    if (!this.$store.getters.isStatusFinished(this.run?.status)) {
      this.continuouslyUpdateRun();
    }
  },

  destroyed() {
    clearTimeout(this.timerId);
  },

  methods: {
    listRuns() {
      this.$router.push({ name: "Runs" });
    },

    onEditConfirm() {
      put(
        "/simulationRun/rename?runId=" +
          encodeURIComponent(this.run?.id) +
          "&name=" +
          encodeURIComponent(this.editingName)
      )
        .then((r) => {
          this.editingName = r;
          this.run.title = r;
          this.showEdit = false;
        })
        .catch((e) => {
          this.$store.commit("showError", e);
          this.showEdit = false;
        });
    },

    editRun() {
      this.showEdit = true;
    },

    async deleteRun() {
      if (!this.run.id) return;

      this.deleting = true;
      await remove("/simulationRun?id=" + this.run.id)
        .then(() => {
          this.$router.push({ name: "Runs" });
          this.deleting = false;
        })
        .catch((error) => {
          this.onError(error);
          this.deleting = false;
        });
    },

    async getRun(id) {
      if (!id) return;

      return await get("/simulationRun?id=" + id).catch((e) => this.onError(e));
    },

    async onCancelRun() {
      await post("/simulationRun/" + this.run.infoId + "/cancel").catch(
        (error) => this.onError(error)
      );
      this.run = await this.getRun(this.run.id);
    },

    async onExportOlp() {
      this.exportingOlp = true;
      downloadFile("/simulationRun/" + this.run.infoId + "/export-olp")
        .then(() => (this.exportingOlp = false))
        .catch((err) => {
          this.$store.commit("showError", err);
          this.exportingOlp = false;
        });
    },

    async onStartRun() {
      await get("/simulationRun/run?id=" + this.run.id).catch((error) =>
        this.onError(error)
      );
      this.run = await this.getRun(this.run.id);
    },

    onError(error) {
      this.$store.commit("showError", error);
    },

    continuouslyUpdateRun() {
      this.timerId = window.setTimeout(async () => {
        if (this.run?.id) {
          if (!this.deleting) {
            this.run = await this.getRun(this.run.id);
          }
          if (!this.$store.getters.isStatusFinished(this.run?.status)) {
            this.continuouslyUpdateRun();
          }
        }
      }, 1800);
    },

    copyRun() {
      this.$router.push({
        name: "Copy Run",
        params: { runId: this.$route.params.runId },
      });
    },
  },
};
</script>

<style>
.loader {
  width: 30px;
  aspect-ratio: 4;
  --_g: no-repeat radial-gradient(circle closest-side, #000 90%, #0000);
  background: var(--_g) 0% 50%, var(--_g) 50% 50%, var(--_g) 100% 50%;
  background-size: calc(100% / 3) 100%;
  animation: l7 1s infinite linear;
}
@keyframes l7 {
  33% {
    background-size: calc(100% / 3) 0%, calc(100% / 3) 100%, calc(100% / 3) 100%;
  }
  50% {
    background-size: calc(100% / 3) 100%, calc(100% / 3) 0%, calc(100% / 3) 100%;
  }
  66% {
    background-size: calc(100% / 3) 100%, calc(100% / 3) 100%, calc(100% / 3) 0%;
  }
}
</style>
